<template>
  <div
    ref="dropdownRef"
    class="change-diet-status"
    :class="[
      `change-diet-status--trigger-${getDietStatusVariant(dietStatus)}`,
      { 'change-diet-status--open': isOpen && dietStatus !== 3 }
    ]"
  >
    <Tooltip placement="bottom-end">
      <template #trigger>
        <StyledButton
          class="change-diet-status__trigger min-w-0"
          variant="secondary"
          @click="handleClick"
        >
          {{ statusLabel }}
          <BaseIcon name="expand_more" type="secondary" class="change-diet-status__chevron" />
        </StyledButton>
      </template>
      Zmień status
    </Tooltip>
    <DietStatusMenu v-if="isOpen" :options="availableOptions" @select="select" />
  </div>

  <ArchiveDietModal
    v-if="showArchiveModal"
    :diet-id="dietId"
    @close="showArchiveModal = false"
    @success="handleArchiveSuccess"
  />
</template>

<script lang="ts" setup>
  import { useMutation, useQueryClient } from '@tanstack/vue-query'
  import { onClickOutside } from '@vueuse/core'
  import { computed, ref } from 'vue'

  import BaseIcon from '@/components/BaseIcon.vue'
  import { StyledButton } from '@/components/StyledButton'
  import { Tooltip } from '@/components/ui'
  import { ArchiveDietModal } from '@/pages/diets/components/ArchiveDietModal'
  import { dietsService } from '@/services/dietsService'
  import { useGlobalStore } from '@/store/globalStore'
  import { reportError } from '@/utils/reportError'

  import type { DietStatus } from '@/types/Diet'

  import DietStatusMenu from './components/DietStatusMenu/DietStatusMenu.vue'
  import { DIET_STATUS_OPTIONS, getDietStatusLabel, getDietStatusVariant } from './status'

  const props = defineProps<{
    dietId: number
    dietStatus: DietStatus
  }>()

  const emit = defineEmits<{
    change: [status: DietStatus]
    refresh: []
  }>()

  const globalStore = useGlobalStore()
  const queryClient = useQueryClient()
  const dropdownRef = ref<HTMLElement | null>(null)
  const isOpen = ref(false)
  const showArchiveModal = ref(false)

  const updateStatusMutation = useMutation({
    mutationFn: async ({ dietId, status }: { dietId: number; status: number }) => {
      await dietsService.updateDietStatus(dietId, status)
    },
    onError: (error: unknown) => {
      globalStore.createMessage({
        title: 'Błąd podczas zmiany statusu jadłospisu',
      })
      reportError(error, 'Error while updating diet status', {
        dietId: props.dietId,
      })
    },
    onSuccess: (_, { status }) => {
      let title: string

      if (status === 3) {
        title = 'Jadłospis został zarchiwizowany'
      } else if (status === 2) {
        title = 'Status jadłospisu został zmieniony na Gotowy'
      } else if (status === 1) {
        title = 'Status jadłospisu został zmieniony na Szkic'
      } else {
        title = 'Status jadłospisu został zmieniony'
      }

      globalStore.createMessage({
        title,
      })
      void queryClient.invalidateQueries({ queryKey: ['patientMealPlans'] })
      emit('refresh')
    },
  })

  onClickOutside(dropdownRef, () => {
    isOpen.value = false
  })

  const statusLabel = computed(() => getDietStatusLabel(props.dietStatus))

  const availableOptions = computed(() =>
    DIET_STATUS_OPTIONS.filter(option => option.value !== props.dietStatus),
  )

  function handleClick() {
    isOpen.value = !isOpen.value
  }

  function select(value: DietStatus) {
    if (value === 3) {
      // Show archive confirmation modal
      showArchiveModal.value = true
      isOpen.value = false
    } else {
      updateStatusMutation.mutate({ dietId: props.dietId, status: value })
      emit('change', value)
      isOpen.value = false
    }
  }

  function handleArchiveSuccess() {
    emit('change', 3)
    emit('refresh')
  }
</script>

<style scoped>
  .change-diet-status {
    position: relative;
    display: inline-flex;
  }

  /* Trigger: status-based colors (draft = orange, ready = green) */
  .change-diet-status--trigger-draft :deep(.change-diet-status__trigger) {
    color: #b54708;
    background-color: #fff9ed;
    border: 1px solid #e4a42c;
    box-shadow: 0 1px 3px hsl(0deg 0% 0% / 8%);
  }

  .change-diet-status--trigger-draft
    :deep(.change-diet-status__trigger .change-diet-status__chevron) {
    color: #e4a42c;
  }

  .change-diet-status--trigger-ready :deep(.change-diet-status__trigger) {
    color: #067647;
    background-color: #ebf9ed;
    border: 1px solid #349b44;
    box-shadow: 0 1px 3px hsl(0deg 0% 0% / 8%);
  }

  .change-diet-status--trigger-archived :deep(.change-diet-status__trigger) {
    color: #1a1a1a;
    background-color: #f8f8f8;
    border: 1px solid #cacaca;
    box-shadow: 0 1px 3px hsl(0deg 0% 0% / 8%);
    opacity: 1;
  }

  .change-diet-status--trigger-ready
    :deep(.change-diet-status__trigger .change-diet-status__chevron) {
    color: #349b44;
  }

  .change-diet-status__chevron {
    margin-left: 4px;
    color: inherit;
    transition: transform 0.2s;
  }

  .change-diet-status--open :deep(.change-diet-status__chevron) {
    transform: rotate(180deg);
  }

  .change-diet-status__dropdown {
    position: absolute;
    top: 100%;
    right: 0;
    z-index: 50;
    display: flex;
    flex-direction: column;
    gap: 6px;
    align-items: end;
    min-width: 100%;
    padding: 0.5rem;
    margin-top: 6px;
    background-color: var(--background-bg-primary, #fff);
    border: 1px solid var(--border-border-secondary, #e5e7eb);
    border-radius: 0.5rem;
    box-shadow:
      0 4px 6px -1px rgb(10 13 18 / 10%),
      0 2px 4px -2px rgb(10 13 18 / 6%);
  }

  .change-diet-status__option {
    display: flex;
    gap: 4px;
    align-items: center;
    width: 100%;
    padding: 8px 12px;
    font-size: 14px;
    font-weight: 500;
    text-align: left;
    white-space: nowrap;
    cursor: pointer;
    border: 1px solid;
    border-radius: 6px;
    box-shadow: 0 1px 3px hsl(0deg 0% 0% / 8%);
    transition: filter 0.15s;
  }

  .change-diet-status__option:hover {
    filter: brightness(0.97);
  }

  .change-diet-status__option-icon {
    flex-shrink: 0;
  }

  .change-diet-status__option--draft {
    color: #b54708;
    background-color: #fff9ed;
    border-color: #e4a42c;
  }

  .change-diet-status__option--draft .change-diet-status__option-icon {
    color: #e4a42c;
  }

  .change-diet-status__option--ready {
    color: #067647;
    background-color: #ebf9ed;
    border-color: #349b44;
  }

  .change-diet-status__option--ready .change-diet-status__option-icon {
    color: #349b44;
  }

  .change-diet-status__option--archived {
    color: #1a1a1a;
    background-color: #f8f8f8;
    border-color: #cacaca;
  }

  .change-diet-status__option--archived .change-diet-status__option-icon {
    color: #6b7280;
  }
</style>
